"use client";
import blogsData from "@/data/blogs.json";
import { getFeaturedBlogs } from "@/lib/api";
import { motion, useInView } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { CardBody, CardContainer, CardItem } from "../ui/3d-card";
import { getMediaUrl } from "@/lib/utils";

const BlogSection = ({ data }) => {
  // Fallback to hardcoded values if data is not available
  const title = data?.title || "احدث المقالات";
  const subtitle = data?.subtitle || "اطلع على اخر الاخبار";

  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true });
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedBlogs = async () => {
      try {
        setLoading(true);
        // Try to get featured blogs from backend
        const featuredBlogs = await getFeaturedBlogs("ar", 3);

        console.log("[BlogSection] Featured blogs response:", featuredBlogs);
        console.log("[BlogSection] Is array:", Array.isArray(featuredBlogs));
        console.log("[BlogSection] Length:", featuredBlogs?.length);

        // Use backend data if available, otherwise fallback to static JSON
        if (Array.isArray(featuredBlogs) && featuredBlogs.length > 0) {
          console.log(
            "[BlogSection] Using backend featured blogs:",
            featuredBlogs.length
          );
          setArticles(featuredBlogs);
        } else {
          // Fallback to static JSON (take first 3)
          console.log(
            "[BlogSection] Using static JSON fallback - no featured blogs found"
          );
          setArticles(blogsData.slice(0, 3));
        }
      } catch (error) {
        console.error("[BlogSection] Error fetching featured blogs:", error);
        console.error("[BlogSection] Error details:", error.response?.data);
        // Fallback to static JSON
        console.log("[BlogSection] Error occurred, using static JSON fallback");
        setArticles(blogsData.slice(0, 3));
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedBlogs();
  }, []);

  return (
    <section ref={sectionRef} className="py-20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-6xl font-bold text-yellow-500 mb-6">{title}</h2>
          <p className="text-2xl text-black">{subtitle}</p>
        </motion.div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-xl text-gray-600">جاري تحميل المقالات...</p>
          </div>
        ) : (
          <>
            {/* Articles Grid with 3D Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.isArray(articles) &&
                articles.map((article, index) => (
                  <ThreeDArticleCard
                    key={article.id}
                    article={article}
                    index={index}
                    isInView={isInView}
                  />
                ))}
            </div>

            {/* Show message if no articles */}
            {Array.isArray(articles) && articles.length === 0 && (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">📝</div>
                <p className="text-xl text-gray-600">
                  لا توجد مقالات متاحة حالياً
                </p>
              </div>
            )}
          </>
        )}

        {/* View All Button */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Link href="/news">
            <motion.button
              className="bg-[#0a367b] text-white px-8 py-4 rounded-full font-bold hover:bg-[#0a367b]/90 transition-all duration-300 shadow-lg"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              عرض جميع المقالات
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const ThreeDArticleCard = ({ article, index, isInView }) => {
  const extractText = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") return value.ar || value.en || "";
    return "";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.2 }}
    >
      <CardContainer className="inter-var">
        <CardBody className="bg-white relative group/card hover:shadow-2xl hover:shadow-[#0a367b]/10 border-gray-200 w-auto h-auto rounded-3xl p-6 border">
          {/* Image */}
          <CardItem translateZ="100" className="w-full mt-4">
            {article.image?.url || article.imageBase64 || article.image ? (
              <motion.img
                src={
                  article.image?.url
                    ? article.image.url
                    : article.imageBase64
                    ? article.imageBase64
                    : typeof article.image === "string"
                    ? article.image.startsWith("http")
                      ? article.image
                      : getMediaUrl(article.image.replace(/^\//, ""))
                    : getMediaUrl(article.image)
                }
                alt={extractText(article.title)}
                className="w-full h-64 object-cover"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.6 }}
              />
            ) : (
              <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400 text-4xl">📝</span>
              </div>
            )}
          </CardItem>

          {/* Date */}
          <CardItem translateZ="50" className="text-sm text-gray-500 mt-4">
            {(() => {
              if (!article.date) return "";
              try {
                const dateStr =
                  typeof article.date === "string"
                    ? article.date
                    : String(article.date || "");
                if (!dateStr) return "";
                const d = new Date(dateStr);
                return isNaN(d.getTime())
                  ? dateStr
                  : d.toLocaleDateString("ar-SA", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
              } catch {
                return "";
              }
            })()}
          </CardItem>

          {/* Title */}
          <CardItem
            translateZ="60"
            className="text-xl font-bold text-[#0a367b] mt-2 leading-tight group-hover/card:text-yellow-600 transition-colors duration-300"
          >
            {extractText(article.title)}
          </CardItem>

          {/* Excerpt */}
          <CardItem
            as="p"
            translateZ="60"
            className="text-gray-600 text-sm mt-3 leading-relaxed"
          >
            {extractText(article.excerpt)}
          </CardItem>

          {/* Action Button */}
          <div className="flex justify-between items-center mt-6">
            <CardItem translateZ={20}>
              <Link href={`/news/${article.slug}`}>
                <motion.button
                  className="text-[#0a367b] font-semibold hover:text-yellow-600 transition-colors duration-300 flex items-center space-x-2 space-x-reverse group"
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>اقرأ المزيد</span>
                  <motion.span
                    className="group-hover:translate-x-1 transition-transform duration-300"
                    animate={{ x: [0, 5, 0] }}
                    transition={{
                      duration: 1.5,
                      repeat: Number.POSITIVE_INFINITY,
                    }}
                  >
                    ←
                  </motion.span>
                </motion.button>
              </Link>
            </CardItem>
          </div>

          {/* Decorative Elements */}
          <CardItem translateZ="30">
            <motion.div
              className="absolute top-2 left-2 w-2 h-2 bg-[#dfa428]/60 rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.6, 1, 0.6] }}
              transition={{
                duration: 2,
                repeat: Number.POSITIVE_INFINITY,
                delay: index * 0.3,
              }}
            />
          </CardItem>
        </CardBody>
      </CardContainer>
    </motion.div>
  );
};

export default BlogSection;
